import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USER_CACHE_KEY = 'kj_user_cache';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            // Hidratar de inmediato desde cache para no bloquear con loading
            try {
                const cached = localStorage.getItem(USER_CACHE_KEY);
                if (cached) {
                    setUser(JSON.parse(cached));
                    setLoading(false);
                }
            } catch (e) { /* ignore */ }
            fetchUser();
        } else {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`);
            setUser(response.data);
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(response.data));
        } catch (error) {
            // Si NO hay respuesta (network error / offline), confiamos en el cache local
            const isNetworkError = !error.response;
            if (isNetworkError) {
                try {
                    const cached = localStorage.getItem(USER_CACHE_KEY);
                    if (cached) {
                        setUser(JSON.parse(cached));
                        return;
                    }
                } catch (e) { /* ignore */ }
            }
            // Solo cerrar sesion si el servidor respondio que el token es invalido (401/403)
            const status = error.response?.status;
            if (status === 401 || status === 403) {
                logout();
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, {
                username,
                password
            });
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);
            return { success: true };
        } catch (error) {
            const isNetworkError = !error.response;
            if (isNetworkError) {
                return {
                    success: false,
                    error: 'Sin conexion. Para iniciar sesion por primera vez necesitas internet. Si ya iniciaste sesion antes, simplemente cierra y vuelve a abrir la app.'
                };
            }
            return {
                success: false,
                error: error.response?.data?.detail || 'Error al iniciar sesion'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem(USER_CACHE_KEY);
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
    };

    const hasPermission = (moduleSlug) => {
        if (!user) return false;
        if (user.rol === 'administrador') return true;
        return (user.permisos || []).includes(moduleSlug);
    };

    const isAdmin = user?.rol === 'administrador';

    const value = {
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        hasPermission,
        isAdmin
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
};
